import { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Sparkles, Check, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

// Kept in sync with PlansPanel — both panels advertise the same set.
const PRO_FEATURES: string[] = [
  'Live Prices & AI Sentiment',
  'Real-time News Feed with AI tags',
  'Economic Calendar with Playbooks',
  'Interest Rate Tracker',
  'COT Report with AI signals',
  'AI Chat & Chart Analysis',
  'Global Risk & Macro Panel',
  'Bookmarks & Collections',
  'Priority support',
];

const ELITE_FEATURES: string[] = [
  'Everything in Pro, plus:',
  'Trade Journal — log every trade',
  'MT5 Live Sync — closed trades auto-flow in',
  'Statement & CSV import (any broker)',
  'Goal tracking + Risk Reality',
  'Win-rate, max-DD + symbol analytics',
];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

type CheckoutKey = 'monthly' | 'yearly' | 'elite_monthly' | 'elite_yearly';

/**
 * Full-screen paywall shown when an authenticated user's free trial has ended
 * and they have not converted to a paying plan. Blocks the dashboard entirely.
 */
export function TrialExpired() {
  const { user, signOut } = useAuth();
  const [cadence, setCadence] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState<CheckoutKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const endLabel = formatDate(user?.trialEndsAt);
  const postFunnel = !!user?.trialEndsAt || !!user?.whopMembershipId;

  async function startCheckout(plan: CheckoutKey) {
    setError(null);
    setLoading(plan);
    try {
      const res = await api.post<{ url: string }>('/api/payments/checkout', { plan });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        setError("Couldn't start checkout — try again.");
        setLoading(null);
      }
    } catch {
      setError("Couldn't start checkout — try again.");
      setLoading(null);
    }
  }

  const proPlan: CheckoutKey = cadence === 'yearly' ? 'yearly' : 'monthly';
  const elitePlan: CheckoutKey = cadence === 'yearly' ? 'elite_yearly' : 'elite_monthly';
  const proPrice = cadence === 'yearly' ? '£288' : '£29';
  const elitePrice = cadence === 'yearly' ? '£349' : '£38';
  const priceSuffix = cadence === 'yearly' ? '/yr' : '/mo';

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-3xl"
      >
        {/* Header */}
        <div className="text-center space-y-3 mb-6">
          {postFunnel ? (
            <>
              <span className="inline-flex items-center justify-center size-12 rounded-full bg-accent/15 text-accent">
                <Lock className="size-6" />
              </span>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-fg">
                Your free trial has ended
              </h1>
              <p className="text-sm text-fg-muted leading-relaxed max-w-md mx-auto">
                {endLabel
                  ? `Your trial finished on ${endLabel}. Pick a plan below to restore full access.`
                  : 'Your trial has ended. Pick a plan below to restore full access.'}
              </p>
            </>
          ) : (
            <>
              {/* Free-trial badge — gold-leaf framing on the headline */}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-bull/15 border border-bull/30 text-bull text-2xs font-bold uppercase tracking-wide px-3 py-1">
                <Sparkles className="size-3" />
                Free for 7 days · Cancel anytime
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-fg">
                Start your <span className="text-accent">free 7-day trial</span>
              </h1>
              <p className="text-sm text-fg-muted leading-relaxed max-w-lg mx-auto">
                Full access to TELOS for 7 days. Cancel any time before the trial ends and you won't be charged a penny. Pick Pro for the full intelligence stack, or Elite to add the MT5-synced Trade Journal.
              </p>
            </>
          )}
        </div>

        {/* Cadence toggle */}
        <div className="flex justify-center mb-5">
          <div
            className="relative inline-flex p-1 rounded-full bg-bg-card border border-border"
            role="tablist"
            aria-label="Billing cadence"
          >
            {(['monthly', 'yearly'] as const).map((c) => {
              const active = cadence === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCadence(c)}
                  className={
                    'relative z-10 h-8 px-4 rounded-full text-xs font-semibold transition-colors ' +
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ' +
                    (active ? 'bg-accent text-bg' : 'text-fg-muted hover:text-fg')
                  }
                >
                  {c === 'monthly' ? 'Monthly' : 'Yearly'}
                  {c === 'yearly' && (
                    <span
                      className={cn(
                        'ml-1.5 text-2xs font-bold',
                        active ? 'text-bg/80' : 'text-bull',
                      )}
                    >
                      Save 17%
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Pro + Elite cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Pro */}
          <Card className="relative overflow-hidden border-border h-full flex flex-col">
            <CardBody className="py-7 space-y-5 flex-1 flex flex-col">
              <div>
                <div className="flex items-center gap-2 text-fg-muted">
                  <Sparkles className="size-4" />
                  <span className="text-2xs font-semibold uppercase tracking-wide">Pro</span>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold tabular text-fg">{proPrice}</span>
                  <span className="text-sm text-fg-muted">{priceSuffix}</span>
                </div>
                <p className="text-xs text-fg-muted mt-2 leading-relaxed">
                  Full intelligence stack with AI macro context — live prices, news playbooks, calendar, COT, and the AI chat.
                </p>
              </div>
              <ul className="space-y-2 flex-1">
                {PRO_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-fg">
                    <span className="inline-flex items-center justify-center size-4 rounded-full shrink-0 mt-0.5 bg-bull/15 text-bull">
                      <Check className="size-3" />
                    </span>
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="md"
                className="w-full"
                loading={loading === proPlan}
                onClick={() => startCheckout(proPlan)}
              >
                {cadence === 'yearly' ? 'Start Pro Yearly' : 'Start Pro Monthly'}
              </Button>
            </CardBody>
          </Card>

          {/* Elite */}
          <Card className="relative overflow-hidden border-accent/60 shadow-glow h-full flex flex-col">
            <CardBody className="py-7 space-y-5 flex-1 flex flex-col">
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-accent">
                    <Sparkles className="size-4" />
                    <span className="text-2xs font-semibold uppercase tracking-wide">Elite</span>
                  </div>
                  <span className="text-2xs font-bold uppercase tracking-wide text-accent bg-accent/15 border border-accent/30 rounded-full px-2 py-0.5">
                    + Trade Journal
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold tabular text-fg">{elitePrice}</span>
                  <span className="text-sm text-fg-muted">{priceSuffix}</span>
                </div>
                <p className="text-xs text-fg-muted mt-2 leading-relaxed">
                  Everything in Pro plus the Trade Journal — MT5 Live Sync, goal tracking, risk reality and per-symbol analytics.
                </p>
              </div>
              <ul className="space-y-2 flex-1">
                {ELITE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-fg">
                    <span className="inline-flex items-center justify-center size-4 rounded-full shrink-0 mt-0.5 bg-accent/15 text-accent">
                      <Check className="size-3" />
                    </span>
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="primary"
                size="md"
                className="w-full"
                loading={loading === elitePlan}
                onClick={() => startCheckout(elitePlan)}
              >
                {cadence === 'yearly' ? 'Get Elite Yearly' : 'Get Elite Monthly'}
              </Button>
            </CardBody>
          </Card>
        </div>

        {error && (
          <div className="mt-4 text-2xs text-bear text-center" role="alert">
            {error}
          </div>
        )}

        {/* Sign out + secure badge */}
        <div className="mt-6 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-1.5 text-2xs text-fg-subtle hover:text-fg underline-offset-4 hover:underline focus-visible:outline-none focus-visible:underline"
          >
            <LogOut className="size-3" />
            Sign out
          </button>
          <div className="text-2xs text-fg-subtle text-center">
            Secure checkout via Whop. Educational tool — not financial advice.
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Returns true when the authenticated user is on a non-paying plan and their
 * trial window (`trialEndsAt`) has lapsed. Paying plans (`pro`/`pro_yearly`/
 * `elite`/`elite_yearly`) and admins are never gated.
 */
export function isTrialExpired(user: import('@/lib/types').User | null): boolean {
  if (!user) return false;
  if (user.role === 'admin') return false;
  // Paid tiers require an actual Whop membership on file (or a promoCode for
  // legit comp grants). Plan field alone is not enough — guards against
  // stale records / accidental grants. If the user shows a paid plan but
  // has no Whop subscription AND no promo, they're treated as if free and
  // bounced to the paywall.
  const u = user as unknown as { whopMembershipId?: string | null; promoCode?: string | null };
  const hasWhop = !!u.whopMembershipId;
  const hasPromo = !!u.promoCode;
  const isPaid = user.plan === 'pro' || user.plan === 'pro_yearly' || user.plan === 'elite' || user.plan === 'elite_yearly';
  if (isPaid) {
    if (hasWhop || hasPromo) return false;
    // Paid plan but no active subscription proof → block.
    return true;
  }
  // Non-paying plans: 'free' or 'trial'. Block once the trial deadline passes.
  if (!user.trialEndsAt) {
    return true;
  }
  const end = new Date(user.trialEndsAt).getTime();
  if (!Number.isFinite(end)) return false;
  return end <= Date.now();
}
